<template>
    <div id="chat-vlist-container">
        <UseVirtualList v-if="itens.length" ref="virtualList" :list="itens" :options="opcoesLista" :style="{ height: listHeight }" @scroll.passive="emit('scroll', $event)" class="list-unstyled chat-list chat-user-list px-2 mt-3 custom-scrollbar">
            <template #default="{data: chat}">
                <ContactSearchHeader v-if="chat?.itemType === 'contatos-header'" :key="chat.id" :sem-atendimentos="chat.semAtendimentos" :entrada="chat.entrada" :saindo="chat.saindo"/>
                <ContactSearchLoading v-else-if="chat?.itemType === 'contatos-carregando'" :key="chat.id" :saindo="chat.saindo"/>
                <ContactSearchItem v-else-if="chat?.itemType === 'contato'" :key="chat.id" :linha="chat" :termo="termo" @select="emit('select-contato', $event)"/>
                <ChatItem v-else-if="chat" :key="chat.id" :chat="chat" @select="emit('select-chat', $event)"/>
            </template>
        </UseVirtualList>

        <div v-if="!itens.length" class="empty_state text-center">
            <div class="empty_state_box_img mt-3">
                <img src="/img/empty_state.png" style="width: 50px !important">
            </div>
            <p class="mt-2" style="color: #9aa1b954;">No momento, não existem atendimentos em aberto.</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { UseVirtualList } from '@vueuse/components';
import ChatItem from './ChatItem.vue';
import ContactSearchHeader from './ContactSearchHeader.vue';
import ContactSearchItem from './ContactSearchItem.vue';
import ContactSearchLoading from './ContactSearchLoading.vue';
import { computed, PropType, ref, watch } from 'vue';
import { Chat } from '@/mocks/app/mockChatData';
import { useChatStore } from '@/stores/app/chatStore';
import { useUiStore } from '@/stores/app/uiStore';
import { vListCalcHeight } from '@/utils/v-list-calc-height.composable';
import { alturaLinhaLista, type LinhaBuscaContatos } from '@/composables/useBuscaContatosLista';
import { ResponseContato } from '@/services/contatos/contatos';

const chatStore = useChatStore();
const uiStore = useUiStore();

const props = defineProps({
    chats: { type: Array as PropType<Chat[]>, default: () => [] },
    resetScroll: { type: Number, default: 0 },
    /** Cabeçalho + contatos da busca, renderizados depois dos atendimentos. */
    contatos: { type: Array as PropType<LinhaBuscaContatos[]>, default: () => [] },
    termo: { type: String, default: '' },
});

const emit = defineEmits({ 
    'scroll'(e: Event) { return e },
    'select-chat'(c: Chat) { return c },
    'select-contato'(c: ResponseContato) { return !!c },
});

const itens = computed<(Chat | LinhaBuscaContatos)[]>(() => [...props.chats, ...props.contatos]);
// Objeto estável: o `UseVirtualList` lê as opções uma vez.
const opcoesLista = { itemHeight: (i: number) => alturaLinhaLista(itens.value[i] as { itemType?: string }) };

const virtualList = ref<any>(null);
watch(() => props.resetScroll, () => virtualList.value?.scrollTo({top: 0, behavior: 'smooth'}));

const { listHeight } = vListCalcHeight('chat-vlist-container', chatStore, { vList: virtualList, list: () => itens.value, triggersToRecalc: [() => uiStore.activeTab] });
</script>

<style scoped>
.chat-message-list {
    scrollbar-width: thin;
    scrollbar-color: #cbd2d9 var(--bs-sidebar-sub-bg);
}
.chat-message-list::-webkit-scrollbar {
    width: 7px;
}
.chat-message-list::-webkit-scrollbar-track {
    background: var(--bs-sidebar-sub-bg);
}
.chat-message-list::-webkit-scrollbar-thumb {
    background-color: #cbd2d9;
    border-radius: 10px;
    border: 2px solid var(--bs-sidebar-sub-bg);
}

/* Scrollbar no Tema Escuro */
[data-bs-theme=dark] .chat-message-list {
    scrollbar-color: #4a5562 var(--bs-sidebar-sub-bg);
}
[data-bs-theme=dark] .chat-message-list::-webkit-scrollbar-thumb {
    background-color: #4a5562;
    border-color: var(--bs-sidebar-sub-bg);
}

.custom-scrollbar {
    overflow-y: auto;
    margin-right: 2px;
}
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e7eaee; border-radius: 4px; }
[data-bs-theme="dark"] .custom-scrollbar::-webkit-scrollbar-thumb { background: #3b4045; }
</style>